import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Alert,
  Chip,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import { apiClient } from '../../utils/api';

// Styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
  },
}));

const TemplateCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const TemplatesPage = () => {
  const { projectId } = useParams();
  const { project } = useOutletContext();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [seleccionPm, setSeleccionPm] = useState(null);
  const [clientName, setClientName] = useState(null);

  useEffect(() => {
    fetchTemplates();
    fetchStakeholderInfo();
  }, [projectId]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/prepare-phase/${projectId}/templates`);

      if (response.data.success) {
        setTemplates(response.data.templates);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchStakeholderInfo = async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}/stakeholders`);
      if (!response.data.success) return;
      const stakeholders = response.data.stakeholders || [];
      let pm = null;
      let client = null;
      for (const s of stakeholders) {
        const orgLower = (s.organization || '').toLowerCase();
        const roleLower = (s.role || '').toLowerCase().trim();
        const isSeleccionOrg = orgLower.includes('seleccion') || orgLower.includes('selección');
        const isSeleccionRole = roleLower.includes('seleccion') || roleLower.includes('selección');
        const isSeleccion = isSeleccionOrg || isSeleccionRole;
        const isPm = roleLower.includes('project manager') || roleLower === 'pm';
        if (isSeleccion && isPm) {
          pm = s.name;
        }
        if (!isSeleccion && s.organization && !client) {
          client = s.organization;
        }
      }
      if (pm) setSeleccionPm(pm);
      if (client) setClientName(client);
    } catch (err) {
      // non-critical — fallback to project fields
    }
  };

  const handleDownload = async (templateId) => {
    try {
      setDownloading(prev => ({ ...prev, [templateId]: true }));
      setError('');

      const response = await apiClient.get(
        `/prepare-phase/${projectId}/templates/${templateId}/download`,
        {
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `template_${templateId}.docx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess('Template downloaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error downloading template:', err);
      setError(err.response?.data?.error || 'Failed to download template');
    } finally {
      setDownloading(prev => ({ ...prev, [templateId]: false }));
    }
  };

  const handleNavigateToProjectTeam = () => {
    navigate(`/projects/${projectId}/overview/stakeholders`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <SectionTitle variant="h5">
          <DescriptionIcon />
          Project Charter
        </SectionTitle>
        <Typography variant="body2" color="text.secondary" paragraph>
          Download Project Charter with auto-populated details from your project data
        </Typography>
      </Paper>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Project Info Summary */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: 'action.hover' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Project Information Auto-Populated from Project Team
          </Typography>
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={handleNavigateToProjectTeam}
            variant="outlined"
            color="primary"
          >
            Manage Project Team
          </Button>
        </Box>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">Project Name:</Typography>
            <Typography variant="body2" fontWeight="bold">{project?.name || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">Client Name:</Typography>
            <Typography variant="body2" fontWeight="bold">{clientName || project?.client_name || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">SELECCION PM:</Typography>
            <Typography variant="body2" fontWeight="bold">{seleccionPm || project?.project_manager || 'Not set'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">CPI Number:</Typography>
            <Typography variant="body2" fontWeight="bold">{project?.cpi_project_number || 'Not set'}</Typography>
          </Grid>
        </Grid>
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="caption">
            📋 Template data is automatically populated from your <strong>Project Overview → Project Team</strong> stakeholders.
            Add stakeholders with roles like "SELECCION Project Manager", "Customer Project Manager", "Customer Project Sponsor",
            and "SELECCION Delivery Executive" to auto-fill templates.
          </Typography>
        </Alert>
      </Paper>

      {/* Templates Grid */}
      <Grid container spacing={3}>
        {templates.map((template) => (
          <Grid item xs={12} sm={6} md={4} key={template.id}>
            <TemplateCard>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <DescriptionIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Chip label={template.category} size="small" color="primary" variant="outlined" />
                </Box>
                <Typography variant="h6" gutterBottom>
                  {template.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {template.description}
                </Typography>
              </CardContent>
              <Divider />
              <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  variant="contained"
                  onClick={() => handleDownload(template.id)}
                  disabled={downloading[template.id]}
                >
                  {downloading[template.id] ? 'Downloading...' : 'Download'}
                </Button>
              </CardActions>
            </TemplateCard>
          </Grid>
        ))}
      </Grid>

      {templates.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <DescriptionIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No templates available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Templates will be added soon
          </Typography>
        </Paper>
      )}

    </Box>
  );
};

export default TemplatesPage;
